import { createFileRoute } from '@tanstack/react-router';

import {
  type ActiveFilter,
  ActiveFilters,
  FilterBar,
  Group,
  MultiSelectInputView,
  PlanningPageLayoutView,
  SearchInput,
  Stack,
} from '@optiro/ui';

import { ResourceHeatmapView, useResourceHeatmapAdapter } from '@/modules/Planning/ResourceHeatmap';
import { HeatmapViewControls } from '@/modules/Planning/ResourceHeatmap/components/HeatmapViewControls/HeatmapViewControls';
import {
  UTILIZATION_OPTIONS,
  type UtilizationRange,
} from '@/modules/Planning/ResourceHeatmap/hooks/useHeatmapFilters';
import { useHeaderToolbarSlotStore } from '@/stores/headerToolbarSlotStore';

export const Route = createFileRoute('/app/planning/resources')({
  component: RouteComponent,
});

function RouteComponent() {
  const { filters, ...viewProps } = useResourceHeatmapAdapter();
  const headerToolbarSlot = useHeaderToolbarSlotStore((state) => state.element);

  const activeCount = (filters.search ? 1 : 0) + filters.selectedUtilizationRanges.length;

  const pills: ActiveFilter[] = filters.selectedUtilizationRanges.map((range) => ({
    key: range,
    label: UTILIZATION_OPTIONS.find((o) => o.value === range)?.label ?? range,
    onRemove: () =>
      filters.setSelectedUtilizationRanges(
        filters.selectedUtilizationRanges.filter((r) => r !== range)
      ),
  }));

  // ── Slim page toolbar — sits under the header, in the maw-constrained section ──
  const toolbar = (
    <Stack gap="xs">
      <Group gap="md" wrap="wrap" align="center">
        <FilterBar
          gap="xs"
          onClear={filters.clearFilters}
          hasActiveFilters={filters.hasActiveFilters}
          activeCount={activeCount}
        >
          <SearchInput
            collapsible
            placeholder="Søg efter ressource..."
            value={filters.search}
            onChange={filters.setSearch}
            size="xs"
          />
          <MultiSelectInputView
            size="xs"
            placeholder="Filtrér belastning"
            data={UTILIZATION_OPTIONS}
            value={filters.selectedUtilizationRanges}
            onChange={(v) => filters.setSelectedUtilizationRanges(v as UtilizationRange[])}
            w={200}
          />
        </FilterBar>
      </Group>

      <ActiveFilters filters={pills} onClearAll={() => filters.setSelectedUtilizationRanges([])} />
    </Stack>
  );

  return (
    <PlanningPageLayoutView title="Heatmap" toolbar={toolbar}>
      <ResourceHeatmapView
        {...viewProps}
        toolbarTarget={headerToolbarSlot}
        toolbarSection={
          <HeatmapViewControls
            groupBy={filters.groupBy}
            onGroupByChange={filters.setGroupBy}
            timeRange={filters.timeRange}
            onTimeRangeChange={filters.setTimeRange}
          />
        }
      />
    </PlanningPageLayoutView>
  );
}
